import { Button, Container, Nav, Navbar, Offcanvas } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { isMobile } from 'react-device-detect';
import { toast } from 'react-toastify';
import { startLogout } from '../../store/auth';
import axiosApi from '../../axios/axios-api';
import PetroprixLogo from '../../assets/images/petroprix-logo';
import HomeIcon from '../../assets/images/HomeIcon';
import InvoiceIcon from '../../assets/images/InvoiceIcon';
import RefuelingIcon from '../../assets/images/RefuelingIcon';
import PromotionIcon from '../../assets/images/PromotionIcon';
import manualIcon from '../../assets/images/download-pdf-icon.svg';
import HelpIcon from '../../assets/images/HelpIcon';
import BackIcon from '../../assets/images/BackIcon';
import { ValidateToken } from '../../helpers/validateToken';
import { downloadPdf } from '../../helpers/downloadPdf';
import { HelpInfo } from './HelpInfo';
import { LogoPetroprix } from './LogoPetroprix';
import { UserMenu } from './UserMenu';
import { usePageTitle } from '../../hooks/usePageTitle';
import { useLocalizedRoutes } from '../../hooks/useLocalizedRoutes';

const URL_GET_DOWNLOAD_MANUAL = 'flotas/manual';

export const Menu = () => {
  const { t } = useTranslation();
  const { flotas, token } = useSelector((state) => state.auth);
  const dispatch = useDispatch();
  const { title, goBack } = usePageTitle();
  const { getPath, isCurrentRoute } = useLocalizedRoutes();

  const menuItems = [
    {
      routeKey: 'home',
      label: t('menu.item.mis-datos'),
      Icon: HomeIcon,
    },
    {
      routeKey: 'invoices',
      label: t('menu.item.facturas'),
      Icon: InvoiceIcon,
    },
    {
      routeKey: 'refuelings',
      label: t('menu.item.repostajes'),
      Icon: RefuelingIcon,
    },
    /*
    {
      routeKey: 'promotions',
      label: t('menu.item.promociones'),
      Icon: PromotionIcon,
    },
    */
    {
      routeKey: 'help',
      label: t('menu.item.ayuda'),
      Icon: HelpIcon,
    },
  ];

  const handleBackClick = () => {
    window.history.back();
  };

  const downloadManual = async () => {
    try {
      if (process.env.REACT_APP_URL_API && ValidateToken()) {
        const response = await axiosApi.get(URL_GET_DOWNLOAD_MANUAL, {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        });

        if (response?.data?.code === 200) {
          const data = response.data.data;

          if (data.prepago !== null) {
            downloadPdf(data.prepago, 'prepago', 'Manual_flotas');
          }

          if (data.credito !== null) {
            downloadPdf(data.credito, 'credito', 'Manual_flotas');
          }

          return;
        }

        toast(t('facturas.form.table.download-error'), {
          icon: <LogoPetroprix />,
        });
        console.warn(response?.data?.message);
      } else {
        dispatch(startLogout('expired'));
      }
    } catch (error) {
      toast(t('facturas.form.table.download-error'), {
        icon: <LogoPetroprix />,
      });
      console.error(error);
    }
  };

  const renderMenuLink = ({ routeKey, label, Icon }) => {
    const isActive = isCurrentRoute(routeKey);

    return (
      <Nav.Link
        as={Link}
        className='sidebar-menu-item'
        key={routeKey}
        to={getPath(routeKey)}
      >
        <Icon
          width={24}
          height={24}
          color={isActive ? '#db0b27' : '#2B363B'}
        />
        <span className={isActive ? 'menu-item-text-active' : 'menu-item-text'}>
          {label}
        </span>
      </Nav.Link>
    );
  };

  const renderManualSection = () => {
    if (flotas !== '1') {
      return null;
    }

    return (
      <Container className={isMobile ? 'me-auto mt-3 py-1 px-5' : 'me-auto mt-3'}>
        <div className={isMobile ? 'row py-2' : 'row py-2 px-2 menu-manual-container'}>
          <p className='menu-manual-download'>{t('menu.item.manual.info')}</p>
          <Button
            variant='outline-primary'
            size='lg'
            id='menu-manual-btn-download'
            onClick={downloadManual}
            className='menu-manual-btn-download'
          >
            <img src={manualIcon} alt={t('menu.item.manual.icon')} />
            {t('menu.item.manual.button')}
          </Button>
        </div>
      </Container>
    );
  };

  if (isMobile) {
    return (
      <Navbar collapseOnSelect expand='lg' className='menu px-1 d-lg-none d-xl-none '>
        <Container>
          {!goBack ? (
            <Navbar.Brand as={Link} to={getPath('home')}>
              <span>
                <PetroprixLogo size='130' />
                <h6 className='navbar-logo-footer'>{t('menu.footer')}</h6>
              </span>
            </Navbar.Brand>
          ) : (
            <span className='header-title'>
              <span className='header-title-group' onClick={handleBackClick}>
                <BackIcon />
                {title && <h1>{title}</h1>}
              </span>
            </span>
          )}

          <span className='d-flex justify-content-between align-items-center'>
            <HelpInfo />
            <UserMenu />
            <Navbar.Toggle aria-controls='offcanvasNavbar ' />
          </span>
          <Navbar.Offcanvas
            id='offcanvasNavbar'
            aria-labelledby='offcanvasNavbarLabel'
            placement='end'
            className='py-3 px-1 menu-offcanvas-content'
          >
            <Offcanvas.Header closeButton>
              <Offcanvas.Title id='offcanvasNavbarLabel'>
                <span>
                  <PetroprixLogo size='130' />
                  <h6 className='navbar-logo-footer'>{t('menu.footer')}</h6>
                </span>
              </Offcanvas.Title>
            </Offcanvas.Header>
            <Container className='container-menu-mobile'>
              <Navbar.Collapse id='responsive-navbar-nav'>
                <Nav className='mt-3 py-1 px-3'>
                  {menuItems.map(renderMenuLink)}
                  {renderManualSection()}
                </Nav>
              </Navbar.Collapse>
            </Container>
          </Navbar.Offcanvas>
        </Container>
      </Navbar>
    );
  }

  return (
    <Navbar>
      <Container>
        <Navbar.Brand as={Link} to={getPath('home')}>
          <PetroprixLogo className='d-inline-block align-top menu-logo-mobile' />
        </Navbar.Brand>
        <h6 className='navbar-logo-footer'>{t('menu.footer')}</h6>
        <Nav className='me-auto mt-3 py-1'>{menuItems.map(renderMenuLink)}</Nav>
      </Container>
      {renderManualSection()}
    </Navbar>
  );
};
